/* Presentation rules for scores. Pure — no React, no fetch.
 *
 * Every function here exists to stop the UI telling one of the three lies in
 * `docs/design/SCORING.md`, and the first one is the whole file's reason to exist:
 *
 * **A score of `null` must never render as a number.** Not `0`, not `—` in a cell that looks like a
 * failed load, not `100` because "no violations were found". The domain, the schema, the contract and
 * the generated client all carry the absence; this is the last hop, and the one where a `?? 0` looks
 * harmless.
 */

import type { BadgeTone } from '@/shared/ui/StatusBadge';

import type { Coverage, Dimension, DimensionScore, Score, Trend } from '../api/scoring';

/** How a score should be rendered: as a number, or as an explained absence. */
export type ScoreDisplay =
  | { readonly kind: 'measured'; readonly value: number }
  | { readonly kind: 'no-evidence' };

/** A tagged union rather than `number | null`, because a union of two shapes forces a caller to
 * branch while a nullable number invites `?? 0`.
 *
 * The type is the guard. `scoreDisplay(score).value` does not compile, so a component cannot reach a
 * number without having handled the other case first.
 */
export function scoreDisplay(value: number | null): ScoreDisplay {
  return value === null ? { kind: 'no-evidence' } : { kind: 'measured', value };
}

/** Rounded for display only, and at the last possible moment.
 *
 * Nothing upstream rounds — `compute_score` keeps full precision so the decomposition sums exactly,
 * and the schema stores 8 decimals for the same reason. One decimal place is what a report shows; a
 * steward comparing 94.7 to 94.68 is reading noise.
 */
export function formatScore(value: number): string {
  return value.toFixed(1);
}

/** Coverage as a whole percentage, or `null` when there is nothing to divide.
 *
 * `null` propagates rather than becoming `0`: a dataset version with no columns has no coverage to
 * report, and 0% would send someone looking for rules to write against a table that has no fields.
 */
export function formatRatio(ratio: number | null): number | null {
  return ratio === null ? null : Math.round(ratio * 100);
}

/** Tone by **coverage**, not by score.
 *
 * A high score over thin coverage is the misleading case, so the visual weight follows how much of
 * the dataset the number actually describes. Scoring 98 on 4% of the columns should not look
 * reassuring, and colouring by the score is exactly how it would.
 */
export function coverageTone(coverage: Coverage): BadgeTone {
  const ratio = coverage.column_ratio;
  if (ratio === null) return 'unknown';
  if (ratio >= 0.8) return 'ok';
  if (ratio >= 0.4) return 'warn';
  return 'danger';
}

/** Why a dimension has no value. Three different facts, and only one is the customer's to fix.
 *
 * This is the distinction `unmeasurable_dimensions` exists for. Rendering all three as an empty cell
 * would send a data steward looking for rules to write against a dimension Lumiq cannot measure at
 * all.
 */
export type AbsenceReason = 'not-weighted' | 'unmeasurable' | 'no-rules' | 'no-evidence';

export function absenceReason(
  entry: DimensionScore,
  unmeasurable: readonly Dimension[],
): AbsenceReason | null {
  if (entry.value !== null) return null;
  if (entry.weight === 0) return 'not-weighted';
  if (unmeasurable.includes(entry.dimension)) return 'unmeasurable';
  /* Weighted, measurable, and rules ran but none of them measured anything — an empty table, or a
   * task that failed. Distinct from having written no rules at all, because the fix is different:
   * one is "check why the run found no rows", the other is "write a rule". */
  if (entry.rules_without_evidence > 0) return 'no-evidence';
  return 'no-rules';
}

/** i18n key for an absence, so the copy lives in the bundle rather than in a component. */
export function absenceKey(reason: AbsenceReason): string {
  return `scoring.absence.${reason}`;
}

/** Dimensions in a stable, meaningful order: weighted first, then by weight descending.
 *
 * Alphabetical would bury the dimension a tenant cares most about between two they weight at zero.
 * Ties broken by name so two renders of one score never reorder under a reader.
 */
export function orderedDimensions(score: Score): readonly DimensionScore[] {
  return [...score.dimensions].sort((left, right) => {
    if (left.weight !== right.weight) return right.weight - left.weight;
    return left.dimension.localeCompare(right.dimension);
  });
}

/** Whether this score is safe to present as a headline number without qualification.
 *
 * False when the run was partial, when a weighted dimension held no evidence, or when column
 * coverage is below four fifths. Any one of those makes the number describe less than the dataset,
 * and a screen that shows it plain is asserting more than the evidence supports.
 */
export function needsQualifying(score: Score): boolean {
  if (!score.evidence_complete) return true;
  if (score.dimensions_without_evidence.length > 0) return true;
  const ratio = score.coverage.column_ratio;
  return ratio === null || ratio < 0.8;
}

/** The share of the deficit one rule is responsible for, as a percentage of points lost.
 *
 * `null` when nothing was lost — a perfect score has no deficit to apportion, and `0/0` would render
 * as `NaN%`. That is the arithmetic version of the same absence rule.
 */
export function shareOfDeficit(pointsLost: number, totalLost: number): number | null {
  if (totalLost <= 0) return null;
  return Math.round((pointsLost / totalLost) * 100);
}

/** A trend's direction, computed over *measured* points only and never across a model change.
 *
 * Two refusals in one function, and both matter:
 *
 * * Points with no value are skipped. Treating `null` as 0 would make a fortnight of empty tables
 *   look like a collapse and then a recovery.
 * * A window crossing a model change reports `incomparable` rather than a delta. The number moved
 *   because the definition moved, and a delta would attribute that to the data.
 */
export type TrendDirection =
  'improving' | 'declining' | 'flat' | 'incomparable' | 'insufficient';

/** The oldest and newest *measured* points, or `null` when fewer than two were measured.
 *
 * Extracted so `trendDirection` and `deltaDisplay` cannot disagree about which two points a movement
 * is measured between. Two walks of the same series is how one screen reports a rise and another
 * reports a fall from the same payload — the two-vocabularies failure, at the arithmetic.
 */
function measuredEnds(
  trend: Trend,
): { readonly first: number; readonly last: number } | null {
  const measured = trend.points.filter((point) => point.value !== null);
  if (measured.length < 2) return null;
  const first = measured[0]?.value;
  const last = measured[measured.length - 1]?.value;
  if (first === undefined || last === undefined || first === null || last === null) {
    return null;
  }
  return { first, last };
}

/* A tenth of a point is not a movement. Without a threshold, floating-point noise in the last
 * decimal renders as an arrow, and a dashboard full of arrows that mean nothing is worse than no
 * arrows. */
const MOVEMENT_FLOOR = 0.1;

export function trendDirection(trend: Trend): TrendDirection {
  if (trend.crosses_a_model_change) return 'incomparable';
  const ends = measuredEnds(trend);
  if (ends === null) return 'insufficient';
  const delta = ends.last - ends.first;
  if (Math.abs(delta) < MOVEMENT_FLOOR) return 'flat';
  return delta > 0 ? 'improving' : 'declining';
}

/* ---------------------------------------------------------------- the movement, as a figure

 * `trendDirection` answers *which way*; a ledger cell has to print *how far*, and the two must come
 * from one walk of one series. So this is a thin skin over `trendDirection` plus `measuredEnds`
 * rather than its own arithmetic.
 *
 * **Five cases, and four of them are not a number.** `no-comparison` is fewer than two measured
 * points — a dataset scored once has no movement, which is not the same as not having moved.
 * `incomparable` is a window crossing a score-model change: the number moved because the definition
 * moved, and printing a delta would attribute a reweighting to the data. `flat` is a real
 * measurement of no movement, so it is distinct from both. Only `up` and `down` carry a magnitude,
 * and it is always positive — the direction is the arrow's job, so a caller cannot print a minus
 * sign beside a downward triangle.
 */
export type DeltaDisplay =
  | { readonly kind: 'up'; readonly magnitude: number }
  | { readonly kind: 'down'; readonly magnitude: number }
  | { readonly kind: 'flat' }
  | { readonly kind: 'no-comparison' }
  | { readonly kind: 'incomparable' };

export function deltaDisplay(trend: Trend): DeltaDisplay {
  const direction = trendDirection(trend);
  if (direction === 'incomparable') return { kind: 'incomparable' };
  if (direction === 'insufficient') return { kind: 'no-comparison' };
  if (direction === 'flat') return { kind: 'flat' };

  const ends = measuredEnds(trend);
  /* Unreachable: `trendDirection` only returns improving/declining when `measuredEnds` found a pair.
   * Answered as an absence rather than with a non-null assertion, because the honest reading of "we
   * could not find two points" is "there is no comparison" and never a fabricated zero. */
  if (ends === null) return { kind: 'no-comparison' };

  const delta = ends.last - ends.first;
  return delta > 0
    ? { kind: 'up', magnitude: delta }
    : { kind: 'down', magnitude: Math.abs(delta) };
}

/* ---------------------------------------------------------------- bands, mirrored from ScoreBar

 * `ScoreBar` thresholds a **ratio** at 0.9 and 0.7 and keeps those numbers private. These are the
 * same two boundaries on the 0–100 scale a score is expressed in, so a bar and a figure beside it
 * cannot disagree about what "good" means. `presentation.test.ts` pins both boundaries; if
 * `ScoreBar`'s constants ever move, that test is the thing that has to be edited alongside them.
 *
 * Duplicated rather than imported because importing would mean widening `shared/ui`'s surface to
 * export two numbers, and the pinning test is the cheaper half of that trade. Stated so the
 * duplication is a decision rather than an oversight.
 */
export const GOOD_SCORE = 90;
export const POOR_SCORE = 70;

export function scoreBand(value: number): 'ok' | 'warn' | 'danger' {
  if (value >= GOOD_SCORE) return 'ok';
  if (value >= POOR_SCORE) return 'warn';
  return 'danger';
}

/** The dimension dragging a score down, or `null` when nothing is dragging it.
 *
 * **`null` when every measured dimension is already good**, and that guard is the whole point. The
 * decomposition marks the worst dimension in red, and on a dataset scoring 96/95/94 the lowest of
 * three fine figures is not a finding — a red 94 would send a steward to investigate a dimension
 * that is working. So the worst is only named when it is actually below the good band.
 *
 * Unmeasured dimensions are skipped rather than treated as zero: a dimension with no evidence is a
 * coverage gap, which is a different job from a low score, and ranking it worst would bury the
 * dimension that genuinely is.
 *
 * Ties keep the first in `orderedDimensions` order, so two renders of one score never move the mark.
 */
export function worstDimension(score: Score): Dimension | null {
  let worst: Dimension | null = null;
  let lowest = Number.POSITIVE_INFINITY;
  for (const entry of orderedDimensions(score)) {
    if (entry.value === null) continue;
    if (entry.value < lowest) {
      lowest = entry.value;
      worst = entry.dimension;
    }
  }
  if (worst === null) return null;
  return scoreBand(lowest) === 'ok' ? null : worst;
}

/* ---------------------------------------------------------------- an unscored row's reading

 * Three facts, and the one the listing used to give for all three was "Never scored".
 *
 * A dataset nobody has looked at needs a profile run; one profiled with no rules written needs
 * somebody to write a rule; one profiled with a baseline has a *provisional* figure from statistics
 * alone. Different jobs, so different words — and the baseline is prefixed and muted rather than
 * shown plain, because a number from the profile is not evidence of quality.
 *
 * Extracted from the JSX it used to live in as an inline IIFE, so the branches are testable. The
 * assertion that matters most: **a baseline of `0` is a baseline, not an absence.** `?? null` on a
 * number is how a measured zero becomes "never profiled", which is this codebase's most repeated
 * defect wearing a nullish coalesce.
 */
export type UnscoredReading =
  | { readonly kind: 'baseline'; readonly value: number }
  | { readonly kind: 'profiled' }
  | { readonly kind: 'never' };

export function unscoredReading(
  baseline: number | null,
  lastProfiledAt: string | null,
): UnscoredReading {
  if (baseline !== null) return { kind: 'baseline', value: baseline };
  /* Profiled and no baseline is an empty relation — every column came back unmeasured — so it says
   * "profiled" rather than showing a figure. Not a zero: see `ProfileBaseline.value`. */
  if (lastProfiledAt !== null) return { kind: 'profiled' };
  return { kind: 'never' };
}

/* ---------------------------------------------------------------- the diagnosis

 * The one sentence a decomposition table is *for*.
 *
 * A per-rule table answers "what cost us" only if somebody reads every row and adds up the
 * dimensions in their head. The question a steward actually arrives with is narrower: **which
 * dimension is dragging this score, and which rule inside it.** That is derivable from the same
 * contributions, and deriving it here rather than in JSX keeps it testable and stops two screens
 * disagreeing about what "the worst" means.
 *
 * `null` when there is nothing to diagnose, which is two distinct cases and both must stay silent: a
 * perfect score has no deficit to apportion, and a score with no contributions at all was computed
 * over no evidence. Neither is "validity is fine" — they are "there is nothing to say", and a
 * diagnosis line that appeared anyway would be a claim about a measurement nobody made. Same rule
 * `shareOfDeficit` already follows, for the same reason: `0/0` renders as `NaN%`.
 */
export interface Diagnosis {
  readonly dimension: Dimension;
  /** The rule inside that dimension that cost the most. */
  readonly ruleName: string;
  readonly violationCount: number;
  readonly rowsEvaluated: number;
  /** The dimension's share of the whole deficit, as a percentage. */
  readonly share: number;
}

export function diagnose(score: Score): Diagnosis | null {
  const totalLost = score.contributions.reduce(
    (sum, entry) => sum + entry.points_lost,
    0,
  );
  if (totalLost <= 0) return null;

  /* Summed per dimension, because the dominant *dimension* is the finding and a single rule can
   * hide it: three validity rules each losing 4 points outrank one completeness rule losing 10, and
   * a reader looking only at the worst row would be sent to completeness. */
  const byDimension = new Map<Dimension, number>();
  for (const entry of score.contributions) {
    byDimension.set(
      entry.dimension,
      (byDimension.get(entry.dimension) ?? 0) + entry.points_lost,
    );
  }

  let worst: Dimension | null = null;
  let worstLost = 0;
  for (const [dimension, lost] of byDimension) {
    /* Strictly greater, so a tie keeps the first — which is `contributions` order, i.e. the
     * server's. Arbitrary either way, and stable is what matters: a diagnosis that named a
     * different dimension on each refetch would read as the estate changing. */
    if (lost > worstLost) {
      worst = dimension;
      worstLost = lost;
    }
  }
  if (worst === null) return null;

  const culprit = score.contributions
    .filter((entry) => entry.dimension === worst)
    .reduce((left, right) => (right.points_lost > left.points_lost ? right : left));

  return {
    dimension: worst,
    ruleName: culprit.rule_name,
    violationCount: culprit.violation_count,
    rowsEvaluated: culprit.rows_evaluated,
    share: Math.round((worstLost / totalLost) * 100),
  };
}

/* ---------------------------------------------------------------- how old a projection is

 * ADR-009 puts `projected_at` on every read model and the UI half of that promise is a sentence a
 * reader can act on. An ISO instant is not that sentence — "2026-08-31T09:14:22Z" tells somebody
 * nothing about whether they are looking at stale figures — so it is reduced to a unit and a count
 * and the wording is left to ICU.
 *
 * **`null` for an instant that will not parse, never a zero age.** An unparseable timestamp means we
 * do not know how old the figures are, and "0 seconds ago" is the most reassuring thing the screen
 * could say about exactly that. The caller renders no staleness clause at all rather than a
 * fabricated one.
 *
 * A *future* instant — clock skew between this browser and the server, which is real and unbounded —
 * reads as one second rather than as a negative age, for the same reason `latest_timestamp` refuses
 * to treat "fresher than possible" as staleness. And an age under a second rounds up to one, because
 * "projected 0 seconds ago" reads as a rendering bug to the person it is meant to reassure.
 */
export interface StalenessAge {
  readonly unit: 'second' | 'minute' | 'hour' | 'day';
  readonly count: number;
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function stalenessAge(projectedAt: string, now: number): StalenessAge | null {
  const at = new Date(projectedAt).getTime();
  if (Number.isNaN(at)) return null;

  const elapsed = Math.max(0, now - at);
  if (elapsed < MINUTE) {
    return { unit: 'second', count: Math.max(1, Math.floor(elapsed / SECOND)) };
  }
  if (elapsed < HOUR) return { unit: 'minute', count: Math.floor(elapsed / MINUTE) };
  if (elapsed < DAY) return { unit: 'hour', count: Math.floor(elapsed / HOUR) };
  return { unit: 'day', count: Math.floor(elapsed / DAY) };
}

/* ---------------------------------------------------------------- profile facts

 * The two fields a row needs from `/catalog/datasets/facts` to tell three absences apart.
 *
 * Typed structurally rather than as the catalog contract's `DatasetFacts`, so `model/` stays free of
 * another module's generated types and a test fixture is two fields rather than eight.
 *
 * `ledgerRows` used to live here and was the Scores screen's whole row model. It has moved to
 * `rows.ts` as `buildRows`, which carries the same two rules — every catalogued dataset is listed,
 * and an unscored one sorts *first* rather than at the bottom of the range — alongside the target,
 * the movement and the staleness the workspace now needs. One row builder, not two: two would be two
 * orderings, and the highlighted row and the panel describing it would eventually disagree.
 */
export interface ProfileFacts {
  readonly profile_baseline: number | null;
  readonly last_profiled_at: string | null;
}
